import { useIOSPresentation } from '../lib/web-presentation';
import { IOSContext } from '../ios-web/components';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icons';
import { useWorkspaceChromeHosts } from './WorkspaceChromeContext';

interface WorkspaceContextOption {
  label: string;
  value: string;
}

interface WorkspaceContextHeaderProps {
  contextIcon?: IconName;
  contextLabel: string;
  contextValue: string;
  contextOptions?: readonly WorkspaceContextOption[];
  onContextChange?: (value: string) => void;
  periodLabel: string;
  periodValue: string;
  periodOptions?: readonly WorkspaceContextOption[];
  onPeriodChange?: (value: string) => void;
  actionLabel?: string;
  actionAccessibleLabel?: string;
  onAction?: () => void;
}

/** Product workspaces keep selection state while presenting it in the shared navbar. */
export function WorkspaceContextHeader(props: WorkspaceContextHeaderProps) {
  const ios = useIOSPresentation();
  const hosts = useWorkspaceChromeHosts();
  if (ios) return hosts?.desktopContext ? createPortal(<IOSContext {...props} />, hosts.desktopContext)
    : !hosts ? <IOSContext {...props} /> : null;
  return <StandardWorkspaceContextHeader {...props} />;
}

function StandardWorkspaceContextHeader({ actionAccessibleLabel, actionLabel, contextLabel, contextOptions,
  contextIcon, contextValue, onAction, onContextChange, onPeriodChange, periodLabel, periodOptions,
  periodValue }: WorkspaceContextHeaderProps) {
  const hosts = useWorkspaceChromeHosts();
  const [localMobileRequested, setLocalMobileRequested] = useState(false);
  const [mobileRendered, setMobileRendered] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const mobileCloseTimerRef = useRef<number | undefined>(undefined);
  const mobileRestoreFocusRef = useRef(false);
  const mobileSwipeStartRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const mobileRequested = hosts ? hosts.activeMobileLayer === 'context' : localMobileRequested;
  const openMobileContext = (trigger: HTMLButtonElement) => {
    mobileTriggerRef.current = trigger;
    mobileRestoreFocusRef.current = false;
    if (hosts) hosts.openMobileLayer('context');
    else setLocalMobileRequested(true);
  };
  const closeMobileContext = (restoreFocus = true) => {
    mobileRestoreFocusRef.current = restoreFocus;
    if (hosts) hosts.closeMobileLayer('context');
    else setLocalMobileRequested(false);
  };
  useEffect(() => {
    if (mobileCloseTimerRef.current) window.clearTimeout(mobileCloseTimerRef.current);
    if (mobileRequested) {
      setMobileRendered(true);
      const frame = window.requestAnimationFrame(() => setMobileVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setMobileVisible(false);
    if (!mobileRendered) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    mobileCloseTimerRef.current = window.setTimeout(() => {
      setMobileRendered(false);
      if (mobileRestoreFocusRef.current) mobileTriggerRef.current?.focus();
      mobileRestoreFocusRef.current = false;
    }, reducedMotion ? 0 : 220);
  }, [mobileRendered, mobileRequested]);
  useEffect(() => {
    if (!mobileRendered) return;
    document.documentElement.classList.add('am-mobile-context-is-open');
    if (mobileVisible) mobileCloseRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeMobileContext(); }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.documentElement.classList.remove('am-mobile-context-is-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileRendered, mobileVisible]);
  useEffect(() => () => {
    if (mobileCloseTimerRef.current) window.clearTimeout(mobileCloseTimerRef.current);
  }, []);
  const currentContextLabel = contextOptions?.find((option) => option.value === contextValue)?.label ?? contextValue;
  const currentPeriodLabel = periodOptions?.find((option) => option.value === periodValue)?.label ?? periodValue;
  const content = <section aria-label="Active tax context" className="workspace-context-header">
    <ContextField icon={contextIcon} kind="subject" label={contextLabel} onChange={onContextChange} options={contextOptions} value={contextValue} />
    <ContextField kind="period" label={periodLabel} onChange={onPeriodChange} options={periodOptions} value={periodValue} />
    {onAction && actionLabel && actionAccessibleLabel && <button aria-label={actionAccessibleLabel} className="workspace-context-header__action" onClick={onAction} type="button">
      <span aria-hidden="true" className="workspace-context-header__plus">+</span><span>{actionLabel}</span>
    </button>}
  </section>;
  const mobileTrigger = <div className="workspace-context-compact">
    <button aria-expanded={mobileVisible} aria-label={`Tax context: ${currentContextLabel}, ${currentPeriodLabel}`}
      className="account-header__mobile-action workspace-context-compact__subject" onClick={event => openMobileContext(event.currentTarget)}
      title={`${contextLabel}: ${currentContextLabel}`} type="button"><Icon data-context-icon={contextIcon}
        name={contextIcon ?? 'users'} size={20} />
      <span className="workspace-context-compact__combined"><Icon name="calendar" size={14} /></span></button>
    <button aria-expanded={mobileVisible} aria-label={`Tax period: ${currentPeriodLabel}`}
      className="account-header__mobile-action workspace-context-compact__period" onClick={event => openMobileContext(event.currentTarget)}
      title={`${periodLabel}: ${currentPeriodLabel}`} type="button"><Icon name="calendar" size={20} /></button>
  </div>;
  const mobileSheet = mobileRendered && createPortal(<div className={`workspace-context-sheet__layer${mobileVisible ? ' is-open' : ''}`}>
    <button aria-label="Dismiss tax context" className="workspace-context-sheet__backdrop"
      onClick={() => closeMobileContext()} type="button" />
    <section aria-label="Choose tax context" aria-modal="true" className="workspace-context-sheet"
      onPointerDown={(event) => { mobileSwipeStartRef.current = { x: event.clientX, y: event.clientY }; }}
      onPointerUp={(event) => {
        const start = mobileSwipeStartRef.current; mobileSwipeStartRef.current = undefined;
        if (!start) return;
        const horizontal = event.clientX - start.x; const vertical = event.clientY - start.y;
        if (vertical > 72 && Math.abs(vertical) > Math.abs(horizontal) * 1.2) closeMobileContext();
      }} role="dialog">
      <header><button aria-label="Close tax context" className="workspace-context-sheet__back"
        onClick={() => closeMobileContext()} ref={mobileCloseRef} type="button"><Icon name="chevron-down" size={22} /></button>
        <div><strong>Tax context</strong><small>Changes stay within the options authorized for this account.</small></div></header>
      <div className="workspace-context-sheet__body">
        <ContextDisclosure label={contextLabel} onChange={onContextChange} options={contextOptions} value={contextValue}
          onSelected={() => undefined} />
        <ContextDisclosure label={periodLabel} onChange={onPeriodChange} options={periodOptions} value={periodValue}
          onSelected={() => undefined} />
      </div>
      {onAction && actionLabel && <footer><button className="workspace-context-sheet__action" onClick={() => { onAction(); closeMobileContext(false); }}
        type="button"><span aria-hidden="true">+</span>{actionLabel}</button></footer>}
    </section>
  </div>, document.body);
  if (!hosts) return content;
  return <>{hosts.desktopContext ? createPortal(content, hosts.desktopContext) : null}
    {hosts.mobileContext ? createPortal(mobileTrigger, hosts.mobileContext) : null}{mobileSheet}</>;
}

function ContextField({ icon, kind, label, onChange, options, value }: { icon?: IconName; kind: 'subject' | 'period'; label: string;
  onChange?: (value: string) => void; options?: readonly WorkspaceContextOption[]; value: string }) {
  const selected = options?.find(option => option.value === value)?.label ?? value;
  return <label className="workspace-context-header__field" data-context-kind={kind} title={`${label}: ${selected}`}>
    <span className="workspace-context-header__label">{label}:</span>
    <span className={`workspace-context-header__icon${icon ? ' workspace-context-header__icon--visible' : ''}`} aria-hidden="true"><Icon data-context-icon={icon}
      name={kind === 'period' ? 'calendar' : icon ?? 'users'} size={20} /></span>
    {options && onChange
      ? <span className="workspace-context-header__select-wrap"><select aria-label={`Active ${label.toLowerCase()}`}
        onChange={(event) => onChange(event.target.value)} value={value}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select><Icon name="chevron-down" size={14} /></span>
      : <strong aria-label={`Active ${label.toLowerCase()}: ${selected}`}>{selected}</strong>}
  </label>;
}

function ContextDisclosure({ label, onChange, onSelected, options, value }: {
  label: string;
  onChange?: (value: string) => void;
  onSelected(): void;
  options?: readonly WorkspaceContextOption[];
  value: string;
}) {
  const [open, setOpen] = useState(true);
  const selected = options?.find((option) => option.value === value)?.label ?? value;
  if (!options || !onChange) return <section className="workspace-context-sheet__fixed">
    <small>{label}</small><strong>{selected}</strong>
  </section>;
  return <details className="workspace-context-sheet__disclosure" onToggle={(event) => setOpen(event.currentTarget.open)} open={open}>
    <summary><span><small>{label}</small><strong>{selected}</strong></span><Icon name="chevron-down" size={18} /></summary>
    <div role="list">{options.map((option) => <button aria-current={option.value === value ? 'true' : undefined}
      key={option.value} onClick={() => { onChange(option.value); onSelected(); }} type="button">
      <span>{option.label}</span>{option.value === value && <Icon name="check" size={18} />}
    </button>)}</div>
  </details>;
}
